// The content of the public /security page ("How onGuard keeps your data safe"), approved by the product owner
// (Hugo Andrade) as the architecture and security FAQ for customers and prospects.
//
// Rules for editing this file:
//  - Every claim is precise and traceable to a file or a merged spec. The PR that introduced the page lists the proof.
//  - No superlatives, no promises of dates. State what the system does.
//  - American English, no em dashes.
//  - The permission table is not written here: it is rendered from portal/shared/permission-map.mjs, so it cannot drift.
//
// A block is a paragraph (string), { list: [...] } (bullets) or { permissions: true } (the generated permission table).

export const SUPPORT_EMAIL = 'support@ipc-global.com';

// ENTRAID-347 redesign (plan C8): the page the sign-in links to as "Trust and security".
export const PAGE_TITLE = 'Trust and security';
export const PAGE_DESCRIPTION =
  'Where onGuard for Entra ID keeps your data, which Microsoft permissions it uses, how credentials are protected, how runs are monitored, who can sign in, and how long data is kept.';

export const SECTIONS = [
  {
    id: 'where-your-data-lives',
    icon: 'sources',
    title: 'Where your data lives',
    items: [
      {
        q: 'Where does the collected data go?',
        a: [
          'The collector is an AWS Lambda function that runs in your AWS account. It reads Microsoft Graph in your Entra ID tenant and writes files straight to an Amazon S3 bucket in the same account. The files do not pass through IPC Global systems, and IPC Global keeps no copy.',
          {
            list: [
              'The bucket is yours: ipc-onguard-entra-<name>-<account id>. It is encrypted at rest (SSE-S3, AES-256), blocks all public access, keeps object versions, and refuses any request that is not over TLS.',
              'Removing onGuard from your account does not delete the bucket or its data. You decide when to delete them.',
            ],
          },
        ],
      },
      {
        q: 'What does the onGuard portal receive?',
        a: [
          'The portal at entraid.onguard.ipc-global.com receives operational information, not your directory data:',
          {
            list: [
              'After each run, a signed run summary: when it ran, how long it took, the status, which data sources succeeded or failed (with a short error message), and how many rows each source produced. It never contains the rows.',
              'If you turn Watch on, one record per detected event: the activity, who did it, what it targeted, what changed, and when, plus the names and email addresses of the people to notify. These records are encrypted in the portal and expire automatically after 13 months.',
              'For compliance reports, a signed completion note with the report size, checksums and summary result. The report files stay in your account.',
            ],
          },
        ],
      },
      {
        q: 'Can IPC Global reach into my AWS account?',
        a: [
          'Only through a role you create when you deploy, named IPCOnGuardTrustRole-<your customer id>. Its limits:',
          {
            list: [
              'Only the onGuard portal can assume it, and only with your customer id as the External ID. Sessions last 15 minutes.',
              'Its permissions are limited to resources whose names start with ipc-onguard-: deploying and updating the onGuard stack and running its functions.',
              'It cannot change the bucket policy or the Object Lock configuration of your compliance evidence bucket.',
              'The role also permits object access on the onGuard buckets, which stack management needs. No portal code reads your data files. You can see every use of the role in your own AWS CloudTrail, and you can delete it at any time to end the portal\'s access.',
            ],
          },
        ],
      },
    ],
  },
  {
    id: 'what-we-read',
    icon: 'security',
    title: 'What we read from Microsoft',
    items: [
      {
        q: 'Which permissions does onGuard use, and why?',
        a: [
          'onGuard uses Microsoft Graph application permissions, which means no user signs in to collect data. Each new customer\'s own app registration declares only the permissions that match the data sources you chose, all of them read-only, and your admin grants consent to exactly that list. The table below lists every permission an app registration can request.',
          { permissions: true },
          'Some permissions return data only if your tenant has the matching Microsoft license (for example Entra ID P2 for risk detections). Without it, that source reports that the license is missing.',
        ],
      },
      {
        q: 'Can onGuard change anything in my tenant?',
        a: [
          'No. Every request the collectors send to Microsoft Graph is a read. The only POST is Graph batching, which carries read requests. The app registration of a new customer holds read permissions only.',
          'Customers who were onboarded before per-customer apps still run on IPC Global\'s shared onGuard app, which also holds Application.ReadWrite.OwnedBy. That permission lets an app manage only the app registrations it owns, and onGuard owns none in your tenant. Your own app registration does not carry it.',
        ],
      },
      {
        q: 'Does the portal read my tenant?',
        a: [
          'Only a small set of reads, and never your users, groups or sign-in logs. The portal checks that consent was granted and that the consenting admin belongs to your tenant and holds an admin role, and it reads your verified domains and organization name. During Watch setup it also reads your directory audit log to suggest who to notify.',
          'Every other read of your tenant happens in your AWS account, in the collector or Watch function, and reaches the portal only through the signed callbacks described above.',
        ],
      },
      {
        q: 'Where do the app registrations live?',
        a: [
          'Each new customer gets its own Entra app registration, created in a dedicated IPC Global publisher tenant (one for production, one for testing). The publisher tenant holds only customer apps and the automation app that manages them. It is not IPC Global\'s corporate tenant. A credential for your app works only in your tenant, because your app is consented only there. Customers onboarded earlier run on IPC Global\'s shared onGuard app until they move to their own.',
        ],
      },
    ],
  },
  {
    id: 'credentials',
    icon: 'deployment',
    title: 'Credentials',
    items: [
      {
        q: 'How does the collector authenticate to AWS?',
        a: [
          'With the IAM role of the Lambda function, using short-lived credentials from AWS STS. There are no AWS access keys. The role can read and write your onGuard bucket, read its own configuration, write its own logs, and schedule its own retries. It cannot reach anything else in your account.',
          'If you enable the optional Qlik S3 connector (see Qlik below), onGuard also creates one IAM user with a read-only access key for that connector. That key exists only when you enable the connector.',
        ],
      },
      {
        q: 'How does the collector authenticate to Microsoft?',
        a: [
          'With a client secret of the onGuard app registration. The secret is stored in AWS Secrets Manager in your AWS account, and the collector reads it from there at run time.',
          'Secrets are rotated automatically. Each secret is valid for 90 days. Rotation starts 30 days before expiry: onGuard creates a new secret, delivers it to your account, verifies that a real run authenticates with it, and only then removes the old one. If a step fails, the old secret stays in place. If rotation has not succeeded 30, 14 and 7 days before expiry, your admins and IPC Global support are emailed.',
        ],
      },
    ],
  },
  {
    id: 'reliability',
    icon: 'overview',
    title: 'Reliability',
    items: [
      {
        q: 'What happens when Microsoft throttles or returns an error?',
        a: [
          'Each Microsoft Graph request is retried up to 5 times with exponential backoff and random jitter. When Microsoft sends a Retry-After value, onGuard waits at least that long. One data source failing does not stop the others: the run finishes and is marked partial.',
        ],
      },
      {
        q: 'What if a whole run fails?',
        a: [
          'The collector makes up to 3 automatic attempts per day: the scheduled run, a retry 10 minutes later, and a final retry 30 minutes after that. A retry writes to the same day\'s files, so nothing is duplicated. A fourth automated attempt is blocked.',
        ],
      },
      {
        q: 'When am I told?',
        a: [
          {
            list: [
              'After the final failed attempt, onGuard emails the alert address configured for your account, and IPC Global operations is alerted. If a scheduled retry never reports, the run is treated as final after 2 hours.',
              'If a customer has had no run for 30 hours, IPC Global operations is alerted.',
              'If secret rotation has not succeeded, expiry notices are sent 30, 14 and 7 days before expiry.',
            ],
          },
        ],
      },
    ],
  },
  {
    id: 'monitoring',
    icon: 'notifications',
    title: 'Monitoring (Watch)',
    items: [
      {
        q: 'What is Watch, and is it on by default?',
        a: [
          'Watch tells the right person within minutes when something sensitive changes in your tenant. It is off until your onGuard admin turns it on. Every 15 minutes, a function in your AWS account reads the Entra directory audit log (and Microsoft Entra ID Protection risk detections, where your license includes them).',
        ],
      },
      {
        q: 'What triggers an alert?',
        a: [
          {
            list: [
              'Your own account: a sign-in method added, removed or changed, a per-user MFA change, a password reset by someone else, an alias added, an account disabled or enabled, a device registered, and a risky sign-in flagged by Microsoft.',
              'Groups: members or owners added or removed, settings changed, a group deleted or restored.',
              'Applications: a credential added, a permission or consent granted, an owner changed, an app registered.',
              'Roles: a directory role assigned or removed, and Privileged Identity Management activations.',
              'Tenant: domain, federation, Conditional Access and authentication methods policy changes, and partner relationship changes.',
              'Guests: a guest user invited.',
            ],
          },
          'Each notice carries the context: the activity, the result, the time, the target, what changed, who did it, and a link to open the event in Microsoft Entra. Risky sign-in notices also name the network and whether the location is a trusted named location.',
        ],
      },
      {
        q: 'Who receives alerts?',
        a: [
          'Every recipient is a user in your directory, never a typed-in address: the person whose account changed, the owners of a changed group, and your tenant guardians. Guardians are up to 10 directory users your admin designates, for example a security lead. Your onGuard admins receive notices about Watch itself, such as a settings change.',
        ],
      },
      {
        q: 'How does the "Was this expected?" flow work?',
        a: [
          'Each notice has a link that asks one question: was this expected? The link carries a random single-use token that travels in the URL fragment and is stored only as a hash. Opening the link changes nothing. Only the answer does, and it is submitted separately. A Yes is recorded with who answered and when. A No opens a case that your guardians and admins can see.',
        ],
      },
      {
        q: 'Is there an audit of what was sent?',
        a: [
          'Yes. The send log lists every notice, to whom, and what happened to it. Answers record who answered and when. Every change to Watch settings and every guardian change raises a notice to your admins. IPC Global operators see only aggregate health, such as last check time and counts, and cannot read event content. Reading Watch data during an incident goes through an audited break-glass role.',
        ],
      },
    ],
  },
  {
    id: 'portal-access',
    icon: 'people',
    title: 'Access to the portal',
    items: [
      {
        q: 'How do people sign in?',
        a: [
          'Through IPC Global\'s central identity provider (login.ipc-global.com). It decides from your email domain which method applies. Email and password sign-in requires an authenticator app (TOTP) code; SMS is not offered. Company single sign-on follows your own identity provider\'s MFA policies. Self sign-up is closed: the first admin is created when you subscribe, and that person adds everyone else.',
          'The portal keeps sign-in tokens in memory only and treats a token as expired at the end of its 60 minute lifetime.',
        ],
      },
      {
        q: 'Who can do what?',
        a: [
          {
            list: [
              'Admin: everything for your own company, including People, the Qlik Cloud section and reports.',
              'Developer: read-only access to the data reference and the implementation footprint, and the implementation footprint report.',
              'Qlik credentials: the Qlik section and the reveal of the connector credential.',
              'Auditor: read-only access to reports, controls and the implementation footprint. The grant expires after 30 days by default and at most 180 days. Only your admin can grant it, and IPC Global operators cannot.',
              'IPC Global operators sign in only with IPC Global single sign-on. They support deployments and see run status. The portal denies them report content and Watch event content.',
            ],
          },
          'A company always keeps at least one admin: removing or demoting the last one is refused. A role change takes effect when the person\'s current token expires, within 60 minutes.',
        ],
      },
      {
        q: 'Is access audited?',
        a: [
          'Role grants and removals are recorded with who made them. Compliance report actions are written to an audit trail kept for seven years. The calls the portal makes into your AWS account appear in your own CloudTrail.',
        ],
      },
    ],
  },
  {
    id: 'retention',
    icon: 'collections',
    title: 'Data retention and deletion',
    items: [
      {
        q: 'How long is data kept?',
        a: [
          'You set a retention policy per data source: either only the latest snapshot, or tiered history. Tiered history keeps daily snapshots for 90 days, month-end snapshots for 2 years and year-end snapshots indefinitely. Watch events are kept for 13 months. Expired data is removed automatically by the collector in your account, in your S3 bucket.',
          'Two things are fixed: compliance report evidence is stored with S3 Object Lock in compliance mode (365 days by default, set when the bucket is created), and retention can be extended but never shortened, and function logs are kept for 90 days.',
        ],
      },
      {
        q: 'What happens to my data if I leave?',
        a: [
          'Decommissioning removes the collector stack. It never deletes your bucket. The data stays in your account until you delete it.',
        ],
      },
    ],
  },
  {
    id: 'qlik',
    icon: 'qlik-cloud',
    title: 'Qlik',
    items: [
      {
        q: 'How does Qlik read my data?',
        a: [
          'Amazon S3 is the destination. If you enable the Qlik integration, onGuard creates one IAM user in your account that can list your onGuard bucket and read its objects, and nothing else. Its access key is stored as an encrypted SecureString in your AWS account, and the portal keeps no copy. Your admin, or someone with the Qlik credentials role, reveals it once and enters it in an Amazon S3 connection in Qlik Cloud.',
        ],
      },
    ],
  },
  {
    id: 'contact',
    icon: 'company-settings',
    title: 'Contact',
    items: [
      {
        q: 'Who do I ask?',
        a: [
          'Send security questions, questionnaires and vulnerability reports to support@ipc-global.com. Start the subject with "Security" so it reaches the right person.',
        ],
      },
    ],
  },
];

export const TOC = SECTIONS.map((s) => ({ id: s.id, label: s.title }));
